import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useOnOutsideClick } from '@src/components/hooks/useOnOutsideClick'

export default function BasketViewer({ label, onOpen, children }) {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef(null)
  const wrapperRef = useRef(null)
  const menuRef = useRef(null)

  const menuIdRef = useRef(`basket-menu-${Math.random().toString(36).slice(2)}`)
  const menuId = menuIdRef.current

  const openMenu = () => {
    if (!isOpen) {
      setIsOpen(true)
      onOpen()
    }
  }

  const closeMenu = () => {
    if (isOpen) {
      setIsOpen(false)
      if (buttonRef.current) buttonRef.current.focus()
    }
  }

  const toggleMenu = () => {
    isOpen ? closeMenu() : openMenu()
  }

  useOnOutsideClick(wrapperRef, (target) => {
    if (!target.closest('.ReactModalPortal')) {
      closeMenu()
    }
  })

  useEffect(() => {
    if (isOpen && menuRef.current) {
      const id = requestAnimationFrame(() => {
        menuRef.current.focus()
      })
      return () => cancelAnimationFrame(id)
    }
  }, [isOpen])

  const onButtonKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      openMenu()
    }
  }

  const onMenuKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation()
      closeMenu()
    }
  }

  return (
    <span ref={wrapperRef}>
      <button
        type="button"
        className={`personalization-menu-button ${isOpen ? 'open' : ''} tag--icon`}
        onClick={toggleMenu}
        onKeyDown={onButtonKeyDown}
        ref={buttonRef}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        aria-label={label}
        id={`${menuId}-button`}
      >
        <span
          className={`govuk-!-margin-right-1 fas ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}
          aria-hidden="true"
          role="img"
        />
        <span className="menu-link">{label}</span>
      </button>

      {isOpen ? (
        <div
          id={menuId}
          className="personalization-menu"
          role="menu"
          aria-labelledby={`${menuId}-button`}
          // Make the container programmatically focusable
          tabIndex={-1}
          ref={menuRef}
          onKeyDown={onMenuKeyDown}
        >
          {children}
        </div>
      ) : null}
    </span>
  )
}

BasketViewer.propTypes = {
  label: PropTypes.string.isRequired,
  onOpen: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
}

BasketViewer.defaultProps = {
  onOpen: () => 0,
}
